import {
  createContext,
  useContext,
  useId,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type LabelHTMLAttributes,
  type ReactNode,
  type Ref,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import { cn } from "../lib/cn";

interface FieldContextValue {
  controlId: string;
  hintId: string | undefined;
  errorId: string | undefined;
  invalid: boolean;
}

const FieldContext = createContext<FieldContextValue | null>(null);

export interface FieldProps {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  /** Explicit id for the control; generated when omitted. */
  id?: string;
  className?: string;
  children: ReactNode;
}

/** Label + control + hint/error, wired with htmlFor / aria-describedby / aria-invalid. */
export function Field({ label, hint, error, id, className, children }: FieldProps) {
  const generated = useId();
  const controlId = id ?? `field-${generated}`;
  const value: FieldContextValue = {
    controlId,
    hintId: hint ? `${controlId}-hint` : undefined,
    errorId: error ? `${controlId}-error` : undefined,
    invalid: Boolean(error),
  };
  return (
    <FieldContext.Provider value={value}>
      <div className={className}>
        <Label htmlFor={controlId}>{label}</Label>
        {children}
        {hint ? <Hint id={value.hintId}>{hint}</Hint> : null}
        {error ? (
          <p id={value.errorId} className="mt-1.5 text-xs font-semibold text-danger" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </FieldContext.Provider>
  );
}

export function Label({ className, children, ...rest }: LabelHTMLAttributes<HTMLLabelElement>) {
  return (
    <label className={cn("label", className)} {...rest}>
      {children}
    </label>
  );
}

export function Hint({ className, ...rest }: HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn("hint", className)} {...rest} />;
}

function useFieldControl(id: string | undefined, describedBy: string | undefined, invalid: boolean | undefined) {
  const field = useContext(FieldContext);
  const describedByIds = [describedBy, field?.hintId, field?.errorId].filter(Boolean).join(" ");
  return {
    id: id ?? field?.controlId,
    "aria-describedby": describedByIds || undefined,
    "aria-invalid": (invalid ?? field?.invalid) === true ? true : undefined,
  };
}

interface ControlExtras {
  invalid?: boolean;
}

export function Input({
  id,
  className,
  invalid,
  ref,
  "aria-describedby": describedBy,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & ControlExtras & { ref?: Ref<HTMLInputElement> }) {
  const control = useFieldControl(id, describedBy, invalid);
  return <input ref={ref} className={cn("input", className)} {...control} {...rest} />;
}

export function Textarea({
  id,
  className,
  invalid,
  ref,
  "aria-describedby": describedBy,
  ...rest
}: TextareaHTMLAttributes<HTMLTextAreaElement> & ControlExtras & { ref?: Ref<HTMLTextAreaElement> }) {
  const control = useFieldControl(id, describedBy, invalid);
  return <textarea ref={ref} className={cn("input", className)} {...control} {...rest} />;
}

export function Select({
  id,
  className,
  invalid,
  ref,
  "aria-describedby": describedBy,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement> & ControlExtras & { ref?: Ref<HTMLSelectElement> }) {
  const control = useFieldControl(id, describedBy, invalid);
  return <select ref={ref} className={cn("input", className)} {...control} {...rest} />;
}
